import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clearBuyInAmount } from 'lib/buyInStorage';
import {
  clearTableSession,
  readTableSession,
  saveTableSession,
} from 'lib/tableSessionStorage';
import { saveLastPlayedTable } from 'lib/lastTableStorage';
import { connectGameSocket, getGameSocket, SOCKET_EVENTS } from 'lib/socket';
import { BattleEndedEvent, SideShowResultEvent, TableChatMessage, TableState } from 'types/game';
import { useSound } from 'context/SoundProvider';
import { useAuth } from 'context/AuthProvider';

/** Fallback until server sends handStartSeconds */
const HAND_COUNTDOWN_DEFAULT = 10;
/** Default turn length — matches backend `turn_expiry_timer` fallback */
const TURN_SECONDS_DEFAULT = 15;

const TURN_PASSING_ACTIONS = new Set([
  'fold',
  'blind',
  'chaal',
  'raise',
  'show',
  'side_show',
  'side_show_accept',
  'side_show_reject',
]);

function resolveHandCountdownMax(seconds: number, configured?: number | null): number {
  if (configured != null && configured >= 1) return configured;
  return Math.max(1, seconds);
}

export function useGameSocket(tableProfileId?: number, buyInAmount?: number, roomCode?: string) {
  const { playTick, resetTick } = useSound();
  const { user, refreshSession, syncTableStack } = useAuth();
  const [connected, setConnected] = useState(false);
  const [tableState, setTableState] = useState<TableState | null>(null);
  const [myCards, setMyCards] = useState<{ code: string }[]>([]);
  const [battleResult, setBattleResult] = useState<BattleEndedEvent | null>(null);
  const [sideShowResult, setSideShowResult] = useState<SideShowResultEvent | null>(null);
  const sideShowClearTimerRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<TableChatMessage[]>([]);
  const [roomClosed, setRoomClosed] = useState<string | null>(null);
  const [turnSeconds, setTurnSeconds] = useState<number | null>(null);
  const [turnMaxSeconds, setTurnMaxSeconds] = useState(15);
  const [handCountdownSeconds, setHandCountdownSeconds] = useState<number | null>(null);
  const [handCountdownMax, setHandCountdownMax] = useState(HAND_COUNTDOWN_DEFAULT);
  const tableProfileIdRef = useRef<number | undefined>(tableProfileId);
  const roomCodeRef = useRef<string | undefined>(roomCode);
  const buyInAmountRef = useRef<number | undefined>(buyInAmount);
  const tableStateRef = useRef<TableState | null>(null);
  const joinPendingRef = useRef(false);
  const timerAnchorRef = useRef<number | null>(null);
  const serverTimerRef = useRef<number | null>(null);
  const timerSeatRef = useRef<number | null>(null);
  const timerPlayerRef = useRef<number | null>(null);
  const handServerRef = useRef<number | null>(null);
  const handAnchorRef = useRef<number | null>(null);
  const handMaxRef = useRef(HAND_COUNTDOWN_DEFAULT);
  const handCountdownActiveRef = useRef(false);
  const handCountdownPausedRef = useRef(false);
  const handPauseStartedRef = useRef<number | null>(null);
  const pendingCardsRef = useRef<{ code: string }[]>([]);
  /** Persists face-up cards for the current hand across betting-round changes */
  const seenCardsRef = useRef<{ code: string }[]>([]);
  const handKeyRef = useRef('');
  const userIdRef = useRef<number | undefined>(user?.userId);
  const wasSeatedRef = useRef(false);
  const leavePendingRef = useRef(false);
  const joinAckAtRef = useRef(0);
  const joinRetryCountRef = useRef(0);
  const lastJoinKeyRef = useRef('');
  const hadConnectionRef = useRef(false);
  const actionInFlightRef = useRef(false);
  const preActionStateRef = useRef<TableState | null>(null);

  const refreshSessionFromTable = useCallback(
    (opts?: Parameters<typeof refreshSession>[0]) => {
      const state = tableStateRef.current;
      const uid = userIdRef.current;
      let merged = opts;
      if (
        state &&
        uid &&
        merged?.liveTableStack == null &&
        merged?.minExpectedWallet == null
      ) {
        const me = state.players.find((p) => p.userId === uid);
        if (me && me.status !== 'left') {
          merged = { ...merged, liveTableStack: me.stack, useCachedWallet: true };
        }
      }
      return refreshSession(merged);
    },
    [refreshSession]
  );

  const turnTimerEpoch = useMemo(() => {
    if (!tableState) return '';
    const seat = tableState.sideShowPending?.targetSeat ?? tableState.activeSeat;
    const uid = tableState.sideShowPending
      ? tableState.players.find(
          (p) => Number(p.seatNo) === Number(tableState.sideShowPending!.targetSeat)
        )?.userId
      : tableState.activePlayerUserId;
    return `${tableState.roundNumber}-${uid ?? 'x'}-${seat ?? 'x'}`;
  }, [tableState]);

  useEffect(() => {
    userIdRef.current = user?.userId;
  }, [user?.userId]);

  const syncVisibleCards = useCallback((state: TableState) => {
    const uid = userIdRef.current;
    if (!uid) {
      setMyCards([]);
      return;
    }

    const handKey = state.battleId > 0 ? String(state.battleId) : '';
    if (handKey && handKey !== handKeyRef.current) {
      handKeyRef.current = handKey;
      pendingCardsRef.current = [];
      seenCardsRef.current = [];
    }

    const me = state.players.find((p) => p.userId === uid);
    const revealing = state.phase === 'showdown' || state.phase === 'completed';

    if (revealing && me?.cards?.length) {
      const revealed = me.cards.filter((c) => c.code && c.code !== 'back' && !c.hidden);
      if (revealed.length) {
        const mapped = revealed.map((c) => ({ code: c.code }));
        seenCardsRef.current = mapped;
        setMyCards(mapped);
        return;
      }
    }

    if (me?.isSeen) {
      const visible =
        pendingCardsRef.current.length > 0
          ? pendingCardsRef.current
          : seenCardsRef.current;
      if (visible.length) {
        setMyCards(visible);
      }
      return;
    }

    if (!revealing) {
      setMyCards([]);
    }
  }, []);

  const clearLocalTimer = useCallback(() => {
    serverTimerRef.current = null;
    timerAnchorRef.current = null;
    timerSeatRef.current = null;
    timerPlayerRef.current = null;
    resetTick();
    setTurnSeconds(null);
  }, [resetTick]);

  const clearHandCountdown = useCallback(() => {
    handServerRef.current = null;
    handAnchorRef.current = null;
    handCountdownActiveRef.current = false;
    setHandCountdownSeconds(null);
  }, []);

  const syncHandCountdown = useCallback((seconds: number, opts?: { force?: boolean }) => {
    if (seconds <= 0) {
      clearHandCountdown();
      return;
    }

    const configuredMax = tableStateRef.current?.handStartSeconds;

    if (opts?.force) {
      const maxSec = resolveHandCountdownMax(seconds, configuredMax);
      handMaxRef.current = maxSec;
      setHandCountdownMax(maxSec);
      handServerRef.current = seconds;
      handAnchorRef.current = Date.now();
      handCountdownActiveRef.current = true;
      setHandCountdownSeconds(seconds);
      return;
    }

    const isNewCycle =
      handServerRef.current == null || seconds > (handServerRef.current ?? 0) + 0.5;
    if (isNewCycle) {
      const maxSec = resolveHandCountdownMax(seconds, configuredMax);
      handMaxRef.current = maxSec;
      setHandCountdownMax(maxSec);
      handServerRef.current = seconds;
      handAnchorRef.current = Date.now();
      handCountdownActiveRef.current = true;
      setHandCountdownSeconds(seconds);
      return;
    }

    if (handAnchorRef.current == null || handServerRef.current == null) {
      handServerRef.current = seconds;
      handAnchorRef.current = Date.now();
      handCountdownActiveRef.current = true;
      setHandCountdownSeconds(seconds);
      return;
    }

    const elapsed = (Date.now() - handAnchorRef.current) / 1000;
    const localLeft = Math.max(0, handServerRef.current - elapsed);
    if (Math.abs(localLeft - seconds) > 0.35) {
      handServerRef.current = seconds;
      handAnchorRef.current = Date.now();
      setHandCountdownSeconds(seconds);
    }
    handCountdownActiveRef.current = true;
  }, [clearHandCountdown]);

  const setHandCountdownPaused = useCallback((paused: boolean) => {
    if (paused === handCountdownPausedRef.current) return;
    if (paused) {
      handPauseStartedRef.current = Date.now();
      handCountdownPausedRef.current = true;
      return;
    }
    if (handPauseStartedRef.current != null && handAnchorRef.current != null) {
      handAnchorRef.current += Date.now() - handPauseStartedRef.current;
    }
    handPauseStartedRef.current = null;
    handCountdownPausedRef.current = false;
    const state = tableStateRef.current;
    const cd = state?.handStartCountdown;
    if (
      cd != null &&
      cd > 0 &&
      (state?.phase === 'waiting' || state?.phase === 'completed' || state?.phase === 'shar')
    ) {
      syncHandCountdown(cd);
    }
  }, [syncHandCountdown]);

  const turnEpochRef = useRef('');

  const syncTimer = useCallback(
    (
      seconds: number | null,
      seatNo?: number | null,
      activePlayerUserId?: number | null,
      epoch?: string
    ) => {
      const nextEpoch = epoch ?? turnEpochRef.current;
      const isNewTurn = Boolean(nextEpoch && nextEpoch !== turnEpochRef.current);

      timerSeatRef.current = seatNo ?? null;
      timerPlayerRef.current = activePlayerUserId ?? null;

      if (isNewTurn) {
        turnEpochRef.current = nextEpoch;
        resetTick();
      }

      if (seconds == null || seconds < 0) {
        if (isNewTurn) {
          serverTimerRef.current = null;
          timerAnchorRef.current = null;
          setTurnSeconds(null);
        }
        return;
      }

      const serverSeconds = Math.max(0, seconds);

      if (isNewTurn) {
        setTurnMaxSeconds(Math.max(TURN_SECONDS_DEFAULT, serverSeconds));
        serverTimerRef.current = serverSeconds;
        timerAnchorRef.current = Date.now();
        setTurnSeconds(serverSeconds);
        return;
      }

      if (timerAnchorRef.current == null || serverTimerRef.current == null) {
        serverTimerRef.current = serverSeconds;
        timerAnchorRef.current = Date.now();
        setTurnSeconds(serverSeconds);
        return;
      }

      const elapsed = (Date.now() - timerAnchorRef.current) / 1000;
      const localLeft = Math.max(0, serverTimerRef.current - elapsed);
      if (Math.abs(localLeft - serverSeconds) > 0.35) {
        serverTimerRef.current = serverSeconds;
        timerAnchorRef.current = Date.now();
        setTurnSeconds(serverSeconds);
      }
    },
    [resetTick]
  );

  const joinTable = useCallback((profileId: number, amount?: number) => {
    const socket = getGameSocket();
    if (!socket?.connected) {
      setError('Not connected to game server');
      return;
    }
    if (joinPendingRef.current) return;

    joinPendingRef.current = true;
    setError(null);
    setBattleResult(null);
    const payload: { tableProfileId: number; buyInAmount?: number } = { tableProfileId: profileId };
    const buyIn = amount ?? buyInAmountRef.current;
    if (buyIn != null && Number.isFinite(Number(buyIn)) && buyIn > 0) {
      payload.buyInAmount = Math.floor(Number(buyIn));
    }
    socket.emit(SOCKET_EVENTS.CLIENT.JOIN_TABLE, payload);
  }, []);

  const joinPrivateTable = useCallback((code: string, amount?: number) => {
    const socket = getGameSocket();
    if (!socket?.connected) {
      setError('Not connected to game server');
      return;
    }
    if (joinPendingRef.current) return;

    joinPendingRef.current = true;
    setError(null);
    setBattleResult(null);
    const payload: { roomCode: string; buyInAmount?: number } = {
      roomCode: code.trim().toUpperCase(),
    };
    const buyIn = amount ?? buyInAmountRef.current;
    if (buyIn != null && Number.isFinite(Number(buyIn)) && buyIn > 0) {
      payload.buyInAmount = Math.floor(Number(buyIn));
    }
    socket.emit(SOCKET_EVENTS.CLIENT.JOIN_PRIVATE_TABLE, payload);
  }, []);

  const rejoinTable = useCallback((battleUuid: string, amount?: number) => {
    const socket = getGameSocket();
    if (!socket?.connected) {
      setError('Not connected to game server');
      return;
    }
    if (!battleUuid || joinPendingRef.current) return;

    joinPendingRef.current = true;
    setError(null);
    const payload: { battleUuid: string; buyInAmount?: number } = { battleUuid };
    const buyIn = amount ?? buyInAmountRef.current;
    if (buyIn != null && Number.isFinite(Number(buyIn)) && buyIn > 0) {
      payload.buyInAmount = Math.floor(Number(buyIn));
    }
    socket.emit(SOCKET_EVENTS.CLIENT.REJOIN_TABLE, payload);
  }, []);

  const attemptTableReconnect = useCallback(() => {
    const uid = userIdRef.current;
    const liveState = tableStateRef.current;
    const liveBattleUuid = liveState?.battleUuid?.trim();
    const liveSeated =
      uid != null &&
      liveState?.players.some((p) => Number(p.userId) === Number(uid) && p.status !== 'left');

    if (liveBattleUuid && liveSeated) {
      if (joinPendingRef.current) return;
      rejoinTable(liveBattleUuid, buyInAmountRef.current ?? undefined);
      return;
    }

    const stored = readTableSession();
    const profileId = tableProfileIdRef.current ?? stored?.tableProfileId;
    const code = roomCodeRef.current?.trim().toUpperCase();

    if (stored?.battleUuid && (!profileId || stored.tableProfileId === profileId)) {
      rejoinTable(stored.battleUuid, buyInAmountRef.current ?? undefined);
      return;
    }

    if (code) {
      joinPrivateTable(code);
      return;
    }
    if (profileId) {
      joinTable(profileId);
    }
  }, [joinTable, joinPrivateTable, rejoinTable]);

  const scheduleJoinRetry = useCallback(() => {
    const profileId = tableProfileIdRef.current;
    if (!profileId || !getGameSocket()?.connected) return;
    if (joinRetryCountRef.current >= 3) return;
    joinRetryCountRef.current += 1;
    joinPendingRef.current = false;
    window.setTimeout(() => {
      if (tableStateRef.current) {
        const uid = userIdRef.current;
        const me =
          uid != null
            ? tableStateRef.current.players.find((p) => Number(p.userId) === Number(uid))
            : undefined;
        if (me && me.status !== 'left') return;
      }
      attemptTableReconnect();
    }, 350);
  }, [attemptTableReconnect]);

  const applyTableState = useCallback(
    (state: TableState) => {
      const players = state.players.map((p) => ({
        ...p,
        userId: Number(p.userId),
        seatNo: Number(p.seatNo),
      }));
      const seated = players.filter((p) => p.status !== 'left');
      let dealerSeat = Number(state.dealerSeat) || 0;
      if (
        (dealerSeat <= 0 || !seated.some((p) => p.seatNo === dealerSeat)) &&
        seated.length > 0
      ) {
        dealerSeat = Math.min(...seated.map((p) => p.seatNo));
      }
      const normalized: TableState = {
        ...state,
        battleId: Number(state.battleId) || 0,
        roundNumber: Number(state.roundNumber) || 0,
        dealerSeat,
        activeSeat: state.activeSeat != null ? Number(state.activeSeat) : null,
        activePlayerUserId:
          state.activePlayerUserId != null ? Number(state.activePlayerUserId) : null,
        players: players.map((p) => ({
          ...p,
          isDealer: p.isDealer || (dealerSeat > 0 && p.seatNo === dealerSeat),
        })),
      };

      actionInFlightRef.current = false;
      preActionStateRef.current = null;

      const uid = userIdRef.current;
      if (uid && joinAckAtRef.current > 0) {
        const me = normalized.players.find((p) => Number(p.userId) === Number(uid));
        const joinSeated = me != null && me.status !== 'left';
        const joinAgeMs = Date.now() - joinAckAtRef.current;
        if (joinSeated) {
          joinAckAtRef.current = 0;
          joinRetryCountRef.current = 0;
        } else if (joinAgeMs < 5000) {
          scheduleJoinRetry();
          // Still apply state — never drop broadcasts that show other seated players
        } else {
          joinAckAtRef.current = 0;
        }
      }

      tableStateRef.current = normalized;
      setTableState(normalized);

      const timerSeat = normalized.sideShowPending?.targetSeat ?? normalized.activeSeat;
      const inTurnPhase = normalized.phase === 'betting' || Boolean(normalized.sideShowPending);
      const turnUserId = normalized.sideShowPending
        ? normalized.players.find(
            (p) => Number(p.seatNo) === Number(normalized.sideShowPending!.targetSeat)
          )?.userId
        : normalized.activePlayerUserId;
      const timerEpoch = `${normalized.roundNumber}-${turnUserId ?? 'none'}-${timerSeat ?? 'none'}`;

      if (inTurnPhase) {
        const seconds =
          normalized.turnSecondsLeft != null && normalized.turnSecondsLeft >= 0
            ? normalized.turnSecondsLeft
            : null;
        syncTimer(seconds, timerSeat, turnUserId ?? normalized.activePlayerUserId, timerEpoch);
      } else {
        if (turnEpochRef.current !== '') {
          turnEpochRef.current = '';
          clearLocalTimer();
        }
      }

      if (uid) {
        const me = normalized.players.find((p) => Number(p.userId) === Number(uid));
        const seated = me != null && me.status !== 'left';
        if (seated && me) {
          syncTableStack(me.stack);
          wasSeatedRef.current = true;
          if (normalized.battleUuid && tableProfileIdRef.current) {
            saveTableSession(normalized.battleUuid, tableProfileIdRef.current);
            saveLastPlayedTable(tableProfileIdRef.current);
          }
        } else if (!seated && wasSeatedRef.current) {
          wasSeatedRef.current = false;
        } else if (!seated) {
          wasSeatedRef.current = false;
        }
        if (me?.status === 'folded' || me?.status === 'left') {
          pendingCardsRef.current = [];
          seenCardsRef.current = [];
          setMyCards([]);
        }
      }

      syncVisibleCards(normalized);
      const betweenHands =
        normalized.phase === 'waiting' ||
        (normalized.phase === 'completed' &&
          normalized.handStartCountdown != null &&
          normalized.handStartCountdown > 0);
      if (betweenHands) {
        pendingCardsRef.current = [];
        seenCardsRef.current = [];
        handKeyRef.current = '';
        setMyCards([]);
      }

      const handCd = normalized.handStartCountdown;
      const endsAt = normalized.handStartEndsAt;
      const countdownPhase =
        normalized.interRoundPhase === 'countdown' ||
        normalized.phase === 'waiting' ||
        normalized.phase === 'shar';

      if (
        normalized.handStartSeconds != null &&
        normalized.handStartSeconds > 0 &&
        countdownPhase
      ) {
        handMaxRef.current = normalized.handStartSeconds;
        setHandCountdownMax(normalized.handStartSeconds);
      }

      const handActive =
        countdownPhase &&
        ((endsAt != null && endsAt > Date.now()) ||
          (handCd != null && handCd > 0));

      if (handActive) {
        if (
          normalized.interRoundPhase === 'countdown' &&
          handCd != null &&
          handCd > 0
        ) {
          syncHandCountdown(handCd, { force: true });
        } else if (
          normalized.interRoundPhase === 'countdown' &&
          endsAt != null &&
          endsAt > Date.now()
        ) {
          syncHandCountdown(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
        } else if (handCd != null && handCd > 0) {
          syncHandCountdown(handCd);
        }
      } else {
        clearHandCountdown();
      }
    },
    [syncTimer, syncVisibleCards, clearLocalTimer, syncHandCountdown, clearHandCountdown, refreshSessionFromTable, syncTableStack, scheduleJoinRetry]
  );

  useEffect(() => {
    tableProfileIdRef.current = tableProfileId;
    roomCodeRef.current = roomCode;
    buyInAmountRef.current = buyInAmount;
    const socket = getGameSocket();
    if (!socket?.connected || joinPendingRef.current) return;

    const joinKey = `${tableProfileId ?? ''}|${roomCode ?? ''}|${buyInAmount ?? ''}`;
    if (lastJoinKeyRef.current === joinKey) return;

    const stored = readTableSession();
    if (stored?.battleUuid && tableProfileId && stored.tableProfileId === tableProfileId) {
      lastJoinKeyRef.current = joinKey;
      rejoinTable(stored.battleUuid, buyInAmount);
    } else if (roomCode) {
      lastJoinKeyRef.current = joinKey;
      joinPrivateTable(roomCode, buyInAmount);
    } else if (tableProfileId) {
      lastJoinKeyRef.current = joinKey;
      joinTable(tableProfileId, buyInAmount);
    }
  }, [connected, tableProfileId, roomCode, buyInAmount, joinTable, joinPrivateTable, rejoinTable]);

  useEffect(() => {
    if (!connected || tableState) return;
    const timer = window.setTimeout(() => {
      if (tableStateRef.current) return;
      if (joinPendingRef.current) return;
      attemptTableReconnect();
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [connected, tableState, attemptTableReconnect]);

  useEffect(() => {
    const timerActive =
      tableState?.phase === 'betting' || Boolean(tableState?.sideShowPending);
    if (!timerActive || serverTimerRef.current == null || timerAnchorRef.current == null) return;

    let raf = 0;
    let lastTickSec = -1;
    const tick = () => {
      if (timerAnchorRef.current == null || serverTimerRef.current == null) return;
      const elapsed = (Date.now() - timerAnchorRef.current) / 1000;
      const left = Math.max(0, serverTimerRef.current - elapsed);
      const sec = Math.ceil(left);
      const myUserId = user?.userId;
      const isMyTurn =
        myUserId != null &&
        timerPlayerRef.current != null &&
        myUserId === timerPlayerRef.current;
      if (sec > 0 && isMyTurn && sec !== lastTickSec) {
        lastTickSec = sec;
        playTick(sec);
      }
      if (left > 0) {
        raf = requestAnimationFrame(tick);
      } else {
        resetTick();
      }
    };
    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, [
    tableState?.phase,
    tableState?.sideShowPending,
    turnTimerEpoch,
    user?.userId,
    playTick,
    resetTick,
  ]);

  useEffect(() => {
    let raf = 0;

    const tick = () => {
      raf = requestAnimationFrame(tick);

      if (!handCountdownActiveRef.current) return;
      if (handServerRef.current == null || handAnchorRef.current == null) return;
      if (handCountdownPausedRef.current) return;

      const elapsed = (Date.now() - handAnchorRef.current) / 1000;
      const left = Math.max(0, handServerRef.current - elapsed);
      setHandCountdownSeconds(left);

      if (left <= 0) {
        handCountdownActiveRef.current = false;
        handServerRef.current = null;
        handAnchorRef.current = null;
        setHandCountdownSeconds(0);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const resyncAfterReconnect = () => {
      const socket = connectGameSocket();
      if (socket && !socket.connected) {
        socket.connect();
        return;
      }
      if (!socket?.connected) return;
      if (joinPendingRef.current) return;

      setConnected(true);
      setError(null);
      lastJoinKeyRef.current = '';
      attemptTableReconnect();

      const state = tableStateRef.current;

      if (state) {
        const uid = userIdRef.current;
        if (uid) {
          const me = state.players.find((p) => Number(p.userId) === Number(uid));
          if (me && me.status !== 'left') {
            syncTableStack(me.stack);
          }
        }

        const endsAt = state.handStartEndsAt;
        const cd = state.handStartCountdown;
        const countdownPhase =
          state.interRoundPhase === 'countdown' ||
          state.phase === 'waiting' ||
          state.phase === 'shar';
        if (countdownPhase) {
          if (endsAt != null && endsAt > Date.now() && state.interRoundPhase === 'countdown') {
            syncHandCountdown(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)), { force: true });
          } else if (cd != null && cd > 0) {
            syncHandCountdown(cd, { force: true });
          }
        }

        const inTurnPhase = state.phase === 'betting' || Boolean(state.sideShowPending);
        if (inTurnPhase && state.turnSecondsLeft != null && state.turnSecondsLeft >= 0) {
          const timerSeat = state.sideShowPending?.targetSeat ?? state.activeSeat;
          const turnUserId = state.sideShowPending
            ? state.players.find(
                (p) => Number(p.seatNo) === Number(state.sideShowPending!.targetSeat)
              )?.userId
            : state.activePlayerUserId;
          syncTimer(
            state.turnSecondsLeft,
            timerSeat,
            turnUserId ?? state.activePlayerUserId,
            `${state.roundNumber}-${turnUserId ?? 'none'}-${timerSeat ?? 'none'}`
          );
        }
      }
    };

    const resyncHandCountdown = () => {
      const state = tableStateRef.current;
      if (!state) return;
      const countdownPhase =
        state.interRoundPhase === 'countdown' ||
        state.phase === 'waiting' ||
        state.phase === 'shar';
      if (!countdownPhase) return;

      const endsAt = state.handStartEndsAt;
      const cd = state.handStartCountdown;
      if (endsAt != null && endsAt > Date.now() && state.interRoundPhase === 'countdown') {
        syncHandCountdown(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
        return;
      }
      if (cd != null && cd > 0) {
        syncHandCountdown(cd);
      }
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        resyncAfterReconnect();
        resyncHandCountdown();
      }
    };

    const onOnline = () => {
      resyncAfterReconnect();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', resyncAfterReconnect);
    window.addEventListener('pageshow', resyncAfterReconnect);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', resyncAfterReconnect);
      window.removeEventListener('pageshow', resyncAfterReconnect);
      window.removeEventListener('online', onOnline);
    };
  }, [syncHandCountdown, syncTimer, attemptTableReconnect, syncTableStack]);

  useEffect(() => {
    const socket = connectGameSocket();
    if (!socket) {
      setError('Sign in required to connect to the game server');
      return;
    }

    const onConnect = () => {
      hadConnectionRef.current = true;
      setConnected(true);
      setError(null);
      joinRetryCountRef.current = 0;
      if (joinPendingRef.current) return;
      attemptTableReconnect();
    };

    const onDisconnect = (reason: string) => {
      setConnected(false);
      joinPendingRef.current = false;
      lastJoinKeyRef.current = '';
      if (reason === 'io server disconnect') return;
      if (hadConnectionRef.current) {
        setError('Connection lost — reconnecting…');
      }
    };

    const onConnectError = () => {
      setConnected(false);
      joinPendingRef.current = false;
      lastJoinKeyRef.current = '';
      if (hadConnectionRef.current) {
        setError('Connection lost — reconnecting…');
      }
    };

    const onTableState = (state: TableState) => {
      joinPendingRef.current = false;
      applyTableState(state);
    };

    const onActionResult = (payload: {
      success?: boolean;
      action?: string;
      message?: string;
      walletBalance?: number;
      cashOut?: number;
    }) => {
      joinPendingRef.current = false;
      if (payload?.success && payload.message === 'Joined table') {
        joinAckAtRef.current = Date.now();
        joinRetryCountRef.current = 0;
        if (tableProfileIdRef.current) {
          clearBuyInAmount(tableProfileIdRef.current);
        }
        const uid = userIdRef.current;
        const liveStack =
          uid != null
            ? tableStateRef.current?.players.find((p) => p.userId === uid)?.stack
            : undefined;
        if (liveStack != null) {
          syncTableStack(Math.max(0, Math.floor(Number(liveStack) || 0)));
        }
        const me =
          uid != null
            ? tableStateRef.current?.players.find((p) => Number(p.userId) === Number(uid))
            : undefined;
        if (!me || me.status === 'left') {
          window.setTimeout(() => {
            if (tableStateRef.current) {
              const seated = tableStateRef.current.players.some(
                (p) => Number(p.userId) === Number(uid) && p.status !== 'left'
              );
              if (seated) return;
            }
            scheduleJoinRetry();
          }, 400);
        }
        return;
      }
      if (payload?.success && payload.message === 'Left table') {
        leavePendingRef.current = false;
        return;
      }
      if (payload?.success && payload.action) {
        // table_state is emitted separately
      }
    };

    const onCards = (payload: { cards: { code: string }[] }) => {
      pendingCardsRef.current = payload.cards;
      seenCardsRef.current = payload.cards;
      const state = tableStateRef.current;
      if (state) syncVisibleCards(state);
    };

    const onTimer = (payload: { secondsLeft: number; seatNo?: number; userId?: number }) => {
      const state = tableStateRef.current;
      if (!state || (state.phase !== 'betting' && !state.sideShowPending)) return;

      if (
        payload.userId != null &&
        state.activePlayerUserId != null &&
        !state.sideShowPending &&
        Number(payload.userId) !== Number(state.activePlayerUserId)
      ) {
        return;
      }
      const expectedSeat = state.sideShowPending?.targetSeat ?? state.activeSeat;
      if (
        payload.userId == null &&
        payload.seatNo != null &&
        expectedSeat != null &&
        Number(payload.seatNo) !== Number(expectedSeat)
      ) {
        return;
      }
      const seatNo = payload.seatNo ?? expectedSeat;
      const turnPlayer =
        payload.userId != null
          ? state.players.find((p) => Number(p.userId) === Number(payload.userId))
          : seatNo != null
            ? state.players.find((p) => Number(p.seatNo) === Number(seatNo))
            : state.sideShowPending != null
              ? state.players.find(
                  (p) => Number(p.seatNo) === Number(state.sideShowPending!.targetSeat)
                )
              : state.players.find((p) => p.userId === state.activePlayerUserId);

      syncTimer(
        payload.secondsLeft,
        seatNo,
        turnPlayer?.userId ?? state.activePlayerUserId,
        `${state.roundNumber}-${turnPlayer?.userId ?? 'none'}-${seatNo ?? 'none'}`
      );
    };

    const onBattleEnded = (payload: BattleEndedEvent) => {
      setBattleResult(payload);
      const uid = userIdRef.current;
      const stackFromPayload =
        uid != null
          ? payload.stacks?.find((s) => Number(s.userId) === Number(uid))?.stack
          : undefined;
      const liveStack =
        stackFromPayload ??
        (uid != null
          ? tableStateRef.current?.players.find((p) => p.userId === uid)?.stack
          : undefined);
      if (liveStack != null) {
        syncTableStack(Math.max(0, Math.floor(Number(liveStack) || 0)));
      }
    };

    const onSideShowResult = (payload: SideShowResultEvent) => {
      setSideShowResult(payload);
      if (sideShowClearTimerRef.current != null) {
        window.clearTimeout(sideShowClearTimerRef.current);
      }
      sideShowClearTimerRef.current = window.setTimeout(() => {
        sideShowClearTimerRef.current = null;
        setSideShowResult(null);
      }, 4200);
    };

    const onError = (payload: { message: string }) => {
      joinPendingRef.current = false;
      if (preActionStateRef.current) {
        const pre = preActionStateRef.current;
        const cur = tableStateRef.current;
        const potGrew = cur != null && Number(cur.potAmount) > Number(pre.potAmount);
        if (!potGrew) {
          tableStateRef.current = pre;
          setTableState(pre);
        }
        preActionStateRef.current = null;
      }
      actionInFlightRef.current = false;
      const uid = userIdRef.current;
      const seated =
        uid != null &&
        tableStateRef.current?.players.some(
          (p) => Number(p.userId) === Number(uid) && p.status !== 'left'
        );
      const rateLimited = /too many requests|slow down/i.test(payload.message || '');
      const staleAction = /player not active/i.test(payload.message || '');
      if (seated && (rateLimited || staleAction)) {
        return;
      }
      lastJoinKeyRef.current = '';
      setError(payload.message);
    };

    const onChatMessage = (message: TableChatMessage) => {
      setChatMessages((prev) => {
        if (prev.some((m) => m.id === message.id)) return prev;
        const next = [...prev, message];
        return next.slice(-50);
      });
    };

    const onChatHistory = (payload: { messages?: TableChatMessage[] }) => {
      if (!Array.isArray(payload?.messages)) return;
      setChatMessages(payload.messages.slice(-50));
    };

    const onRoomClosed = (payload: { reason?: string }) => {
      setRoomClosed(payload?.reason || 'room_closed');
      setError('This private room has been closed');
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on(SOCKET_EVENTS.SERVER.TABLE_STATE, onTableState);
    socket.on(SOCKET_EVENTS.SERVER.ACTION_RESULT, onActionResult);
    socket.on(SOCKET_EVENTS.SERVER.CARDS_DEALT, onCards);
    socket.on(SOCKET_EVENTS.SERVER.TURN_TIMER, onTimer);
    socket.on(SOCKET_EVENTS.SERVER.BATTLE_ENDED, onBattleEnded);
    socket.on(SOCKET_EVENTS.SERVER.SIDE_SHOW_RESULT, onSideShowResult);
    socket.on(SOCKET_EVENTS.SERVER.ERROR, onError);
    socket.on(SOCKET_EVENTS.SERVER.CHAT_MESSAGE, onChatMessage);
    socket.on(SOCKET_EVENTS.SERVER.CHAT_HISTORY, onChatHistory);
    socket.on(SOCKET_EVENTS.SERVER.ROOM_CLOSED, onRoomClosed);

    if (socket.connected) onConnect();

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off(SOCKET_EVENTS.SERVER.TABLE_STATE, onTableState);
      socket.off(SOCKET_EVENTS.SERVER.ACTION_RESULT, onActionResult);
      socket.off(SOCKET_EVENTS.SERVER.CARDS_DEALT, onCards);
      socket.off(SOCKET_EVENTS.SERVER.TURN_TIMER, onTimer);
      socket.off(SOCKET_EVENTS.SERVER.BATTLE_ENDED, onBattleEnded);
      socket.off(SOCKET_EVENTS.SERVER.SIDE_SHOW_RESULT, onSideShowResult);
      socket.off(SOCKET_EVENTS.SERVER.ERROR, onError);
      socket.off(SOCKET_EVENTS.SERVER.CHAT_MESSAGE, onChatMessage);
      socket.off(SOCKET_EVENTS.SERVER.CHAT_HISTORY, onChatHistory);
      socket.off(SOCKET_EVENTS.SERVER.ROOM_CLOSED, onRoomClosed);
      if (sideShowClearTimerRef.current != null) {
        window.clearTimeout(sideShowClearTimerRef.current);
        sideShowClearTimerRef.current = null;
      }
    };
  }, [applyTableState, attemptTableReconnect, scheduleJoinRetry, playTick, syncTimer, syncVisibleCards, refreshSessionFromTable, syncTableStack]);

  const leaveTable = useCallback((battleUuid: string) => {
    if (!battleUuid) return;
    const socket = getGameSocket();
    socket?.emit(SOCKET_EVENTS.CLIENT.LEAVE_TABLE, { battleUuid });
    joinPendingRef.current = false;
    joinAckAtRef.current = 0;
    joinRetryCountRef.current = 0;
    timerAnchorRef.current = null;
    timerSeatRef.current = null;
    timerPlayerRef.current = null;
    turnEpochRef.current = '';
    resetTick();
    pendingCardsRef.current = [];
    seenCardsRef.current = [];
    handKeyRef.current = '';
    setMyCards([]);
    setTurnSeconds(null);
    clearHandCountdown();
    setBattleResult(null);
    setTableState(null);
    tableStateRef.current = null;
    leavePendingRef.current = true;
    clearTableSession();
    wasSeatedRef.current = false;
  }, [clearHandCountdown, resetTick]);

  const sendAction = useCallback((battleUuid: string, action: string, amount?: number) => {
    const socket = getGameSocket();
    if (!socket?.connected) {
      setError('Lost connection — reconnecting…');
      connectGameSocket();
      return;
    }
    const uid = userIdRef.current;
    const me =
      uid != null
        ? tableStateRef.current?.players.find((p) => Number(p.userId) === Number(uid))
        : undefined;
    if (me && (me.status === 'folded' || me.status === 'left')) {
      return;
    }
    const passesTurn = TURN_PASSING_ACTIONS.has(action);
    if (passesTurn && actionInFlightRef.current) {
      return;
    }
    if (
      passesTurn &&
      uid != null &&
      tableStateRef.current?.phase === 'betting' &&
      Number(tableStateRef.current.activePlayerUserId) !== Number(uid)
    ) {
      return;
    }
    setError(null);
    resetTick();
    if (passesTurn && tableStateRef.current) {
      actionInFlightRef.current = true;
      preActionStateRef.current = tableStateRef.current;
      const patched: TableState = {
        ...tableStateRef.current,
        activePlayerUserId: null,
        legalActions: [],
      };
      tableStateRef.current = patched;
      setTableState(patched);
    }
    socket.emit(SOCKET_EVENTS.CLIENT.PLAYER_ACTION, { battleUuid, action, amount });
  }, [resetTick]);

  const sendChatMessage = useCallback(
    (battleUuid: string, payload: { kind: 'text' | 'phrase' | 'sticker'; text?: string; phraseId?: string; stickerId?: string }) => {
      const socket = getGameSocket();
      if (!socket?.connected) {
        setError('Lost connection — reconnecting…');
        connectGameSocket();
        return;
      }
      socket.emit(SOCKET_EVENTS.CLIENT.CHAT_MESSAGE, { battleUuid, ...payload });
    },
    []
  );

  return {
    connected,
    tableState,
    myCards,
    battleResult,
    sideShowResult,
    error,
    chatMessages,
    roomClosed,
    turnSeconds,
    turnMaxSeconds,
    handCountdownSeconds,
    handCountdownMax,
    setHandCountdownPaused,
    joinTable,
    leaveTable,
    sendAction,
    sendChatMessage,
    clearBattleResult: () => setBattleResult(null),
    clearSideShowResult: () => {
      if (sideShowClearTimerRef.current != null) {
        window.clearTimeout(sideShowClearTimerRef.current);
        sideShowClearTimerRef.current = null;
      }
      setSideShowResult(null);
    },
    clearError: () => setError(null),
  };
}
